import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { MapPin, Phone, Mail, Clock, MessageCircle, Send, CheckCircle } from 'lucide-react';
import { shopInfo } from '../data/mock';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission - in real app would send to backend
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', phone: '', email: '', message: '' });
    }, 3000);
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent('Hi! I\'m interested in your IT services/products.');
    window.open(`https://wa.me/${shopInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
  };

  return (
    <section id="contact" className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block text-sm font-semibold text-[#2563eb] bg-[#2563eb]/10 px-4 py-1.5 rounded-full mb-4">
            Get In Touch
          </span>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#1e1919] mb-4">
            Contact Us
          </h2>
          <p className="text-[#736c64] max-w-2xl mx-auto">
            Visit our store or reach out to us. We're here to help you with all your IT and electronics needs.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Info */}
          <div className="space-y-6">
            {/* Contact Cards */}
            <Card className="border-[#2563eb]/10">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#2563eb]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#2563eb]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1e1919] mb-1">Store Address</h4>
                    <p className="text-[#736c64] text-sm">{shopInfo.address}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#2563eb]/10">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#10b981]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#10b981]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1e1919] mb-1">Phone Number</h4>
                    <a href={`tel:${shopInfo.phone}`} className="text-[#2563eb] hover:underline">
                      {shopInfo.phone}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#2563eb]/10">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#f59e0b]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#f59e0b]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1e1919] mb-1">Email Address</h4>
                    <a href={`mailto:${shopInfo.email}`} className="text-[#2563eb] hover:underline text-sm">
                      {shopInfo.email}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#2563eb]/10">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#8b5cf6]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#8b5cf6]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1e1919] mb-2">Business Hours</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between text-[#736c64]">
                        <span>Mon - Sat:</span>
                        <span className="font-medium text-[#1e1919]">{shopInfo.openingHours.weekdays}</span>
                      </div>
                      <div className="flex justify-between text-[#736c64]">
                        <span>Sunday:</span>
                        <span className="font-medium text-red-500">{shopInfo.openingHours.sunday}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* WhatsApp Button */}
            <Button
              onClick={openWhatsApp}
              className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white py-6 text-base font-semibold"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Chat on WhatsApp
            </Button>
          </div>

          {/* Contact Form */}
          <Card className="border-[#2563eb]/10 shadow-lg">
            <CardContent className="p-6 lg:p-8">
              <h3 className="text-xl font-semibold text-[#1e1919] mb-6">
                Send Us a Message
              </h3>

              {submitted ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-[#1e1919] mb-2">Thank You!</h4>
                  <p className="text-[#736c64]">We've received your message and will get back to you soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-[#1e1919] mb-2">
                      Your Name *
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      required
                      className="border-[#2563eb]/20 focus:border-[#2563eb] focus:ring-[#2563eb]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1e1919] mb-2">
                      Phone Number *
                    </label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter your phone number"
                      required
                      className="border-[#2563eb]/20 focus:border-[#2563eb] focus:ring-[#2563eb]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1e1919] mb-2">
                      Email Address
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email (optional)"
                      className="border-[#2563eb]/20 focus:border-[#2563eb] focus:ring-[#2563eb]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1e1919] mb-2">
                      Your Message *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about the product or service you're interested in..."
                      required
                      rows={4}
                      className="border-[#2563eb]/20 focus:border-[#2563eb] focus:ring-[#2563eb] resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white py-6 text-base font-semibold transition-all duration-200 hover:scale-[1.02]"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Send Enquiry
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Map Placeholder */}
        <div className="mt-12 rounded-2xl overflow-hidden bg-[#f7f5f2] h-[300px] flex items-center justify-center border border-[#2563eb]/10">
          <div className="text-center p-8">
            <MapPin className="w-12 h-12 text-[#2563eb] mx-auto mb-4" />
            <h4 className="font-semibold text-[#1e1919] mb-2">Find Us On The Map</h4>
            <p className="text-[#736c64] text-sm mb-4 max-w-md">{shopInfo.address}</p>
            <Button
              variant="outline"
              className="border-[#2563eb] text-[#2563eb] hover:bg-[#2563eb] hover:text-white"
              onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(shopInfo.address)}`, '_blank')}
            >
              Open in Google Maps
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
